import { useEffect, useState } from 'react';

import { api } from '../../api';
import { go, goReplace, hashQuery } from '../../App';
import { useLoad } from '../../data';
import { Loading, PageHead, Panel, fmtMoney } from '../../ui';
import { Product } from '../../types';

// Product catalogue (canon 09 hotel "Product catalogue" · Register) and the
// Product editor (canon 09 "Product editor" · Form). A hotel curates which of
// its connected suppliers' products it books, and the margin on each. The
// supplier's fields are the supplier's — the hotel only ever sets exposure and
// margin, written to its OWN connection row (DECISION-016), never the supplier's.
// One read powers both: GET /v1/products?scope=all — every product an active
// connection could expose, flagged `exposed`. The booking picker keeps the
// default scope (exposed only), so the two never blur.

const KIND_FILTERS: { key: 'all' | 'experience' | 'transfer'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'experience', label: 'Experiences' },
  { key: 'transfer', label: 'Transfers' },
];

export function Products({ editId }: { editId?: string }) {
  const { data, error, reload } = useLoad<{ items: Product[] }>('/products?scope=all');
  const items = data ? data.items : null;
  if (editId) return <Editor items={items} loadError={error} editId={editId} onSaved={reload} />;
  return <Catalogue items={items} loadError={error} />;
}

function Catalogue({ items, loadError }: { items: Product[] | null; loadError: string }) {
  const f = hashQuery().get('f');
  const kind: 'all' | 'experience' | 'transfer' =
    KIND_FILTERS.some((x) => x.key === f) ? (f as 'all' | 'experience' | 'transfer') : 'all';
  const setKind = (key: 'all' | 'experience' | 'transfer') =>
    goReplace(key === 'all' ? '/products' : `/products?f=${key}`);

  const shown = (items ?? [])
    .filter((p) => kind === 'all' || p.kind === kind)
    // Booked products first, then the rest — the catalogue is what's live,
    // with the addable list beneath it. Name-stable within each group.
    .sort((a, b) => Number(!!b.exposed) - Number(!!a.exposed) || a.name.localeCompare(b.name));
  const inCatalogue = (items ?? []).filter((p) => p.exposed).length;

  return (
    <div>
      <PageHead title="Products" sub="Your catalogue — and everything your suppliers offer." />

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {KIND_FILTERS.map((k) => (
          <button key={k.key} className={`fchip ${kind === k.key ? 'active' : ''}`} onClick={() => setKind(k.key)}>
            {k.label}
          </button>
        ))}
      </div>

      {loadError && <p className="error-line">{loadError}</p>}
      {items === null ? (
        <Loading />
      ) : shown.length === 0 ? (
        <div className="empty-neutral">
          No supplier products yet —{' '}
          <button
            type="button"
            onClick={() => go('/suppliers')}
            style={{ background: 'none', border: 'none', color: 'var(--text-link)', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}
          >
            invite a supplier
          </button>{' '}
          to build your catalogue.
        </div>
      ) : (
        <Panel style={{ overflow: 'hidden' }} flush meta={`${inCatalogue} in catalogue`}>
          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th>Supplier</th>
                <th className="num">Supplier price</th>
                <th className="num">Your margin</th>
                <th className="num">Guest pays</th>
                <th>In catalogue</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((p) => (
                <tr key={p.id} className="click" onClick={() => go(`/products/${p.id}`)}>
                  <td>
                    {p.name}
                    <br />
                    <span className="hint">
                      {p.kind === 'transfer' ? 'Transfer' : 'Experience'}
                      {p.durationHours ? ` · ${p.durationHours} h` : ''}
                      {p.maxGuests ? ` · max ${p.maxGuests}` : ''} · per {p.per}
                    </span>
                  </td>
                  <td>{p.supplierName}</td>
                  <td className="num mono">{fmtMoney(p.supplierPrice)}</td>
                  <td className="num mono">{p.exposed && p.margin ? fmtMoney(p.margin) : '—'}</td>
                  <td className="num mono">{p.exposed && p.guestPrice ? fmtMoney(p.guestPrice) : '—'}</td>
                  <td>
                    {p.exposed ? (
                      <span className="chip" style={{ color: 'var(--color-success-dark)', background: 'var(--color-success-light)', borderColor: 'var(--color-success-border)' }}>
                        <span className="chip-dot" style={{ background: 'var(--color-success)' }} />
                        In catalogue
                      </span>
                    ) : (
                      <span className="hint">Add →</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
    </div>
  );
}

// The Form (720 centred, no aside, sticky footer — canon 09). The supplier's
// own fields are read-only here; the hotel edits exposure and margin only.
function Editor({
  items,
  loadError,
  editId,
  onSaved,
}: {
  items: Product[] | null;
  loadError: string;
  editId: string;
  onSaved: () => Promise<void>;
}) {
  const product = items?.find((p) => p.id === editId) ?? null;
  const [exposed, setExposed] = useState<boolean | null>(null);
  const [marginStr, setMarginStr] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Seed the editable state from the product once it has loaded.
  useEffect(() => {
    if (product && exposed === null) {
      setExposed(!!product.exposed);
      setMarginStr(((product.margin?.minor ?? 0) / 100).toFixed(2));
    }
  }, [product, exposed]);

  const back = () => go('/products');
  const currency = product?.supplierPrice.currency ?? 'EUR';
  const parsedMajor = Number.parseFloat(marginStr);
  const marginMinor = Number.isFinite(parsedMajor) ? Math.round(parsedMajor * 100) : NaN;
  const marginOk = Number.isInteger(marginMinor) && marginMinor >= 0;
  const guestMinor =
    product && marginOk ? product.supplierPrice.minor + (exposed ? marginMinor : 0) : product?.supplierPrice.minor ?? 0;

  const save = async () => {
    if (!product || exposed === null) return;
    if (exposed && !marginOk) {
      setError('The margin must be a number of ' + currency + ', zero or more.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      // The connection's exposure is authoritative — rebuild the full set for
      // THIS supplier from the loaded catalogue, then apply this one edit.
      const others = (items ?? [])
        .filter((p) => p.supplierId === product.supplierId && p.exposed && p.id !== editId && p.margin)
        .map((p) => ({ productId: p.id, margin: p.margin! }));
      const next = exposed
        ? [...others, { productId: editId, margin: { minor: marginMinor, currency } }]
        : others;
      await api('PUT', `/connections/${product.supplierId}`, { products: next });
      await onSaved();
      back();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <PageHead title="Product editor" sub={product ? product.supplierName ?? '' : ''} />

      {items === null ? (
        loadError ? <p className="error-line">{loadError}</p> : <Loading />
      ) : !product ? (
        <div className="empty-neutral">
          No such product in your catalogue. <a href="#/products">Back to products</a>
        </div>
      ) : (
        <>
          <Panel title={product.name}>
            {/* The supplier's own fields — theirs to set, shown read-only. */}
            <div className="kv">
              <div className="kvrow">
                <span className="k">Supplier</span>
                <span className="v">{product.supplierName}</span>
              </div>
              <div className="kvrow">
                <span className="k">Kind</span>
                <span className="v">{product.kind === 'transfer' ? 'Transfer' : 'Experience'}</span>
              </div>
              <div className="kvrow">
                <span className="k">Supplier price</span>
                <span className="v mono">{fmtMoney(product.supplierPrice)} · per {product.per}</span>
              </div>
              {(product.durationHours || product.maxGuests) && (
                <div className="kvrow">
                  <span className="k">Detail</span>
                  <span className="v">
                    {product.durationHours ? `${product.durationHours} h` : ''}
                    {product.durationHours && product.maxGuests ? ' · ' : ''}
                    {product.maxGuests ? `max ${product.maxGuests}` : ''}
                  </span>
                </div>
              )}
            </div>
          </Panel>

          <Panel title="In your catalogue">
            <label className="reason" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!exposed}
                onChange={(e) => setExposed(e.target.checked)}
                style={{ marginRight: 8 }}
              />
              Bookable by your desk
            </label>
            <p className="hint" style={{ marginTop: 6 }}>
              Off means it stays out of the booking picker. The supplier never learns your margin.
            </p>

            <div className="field" style={{ marginTop: 16, maxWidth: 260 }}>
              <label htmlFor="margin">Your margin ({currency}, per {product.per})</label>
              <input
                id="margin"
                className="mono"
                type="number"
                min={0}
                step="0.01"
                value={marginStr}
                disabled={!exposed}
                onChange={(e) => setMarginStr(e.target.value)}
              />
            </div>

            <div className="kv" style={{ marginTop: 12 }}>
              <div className="kvrow earn">
                <span className="k">Guest pays{product.per === 'guest' ? ' per person' : ''}</span>
                <span className="v mono">{fmtMoney({ minor: guestMinor, currency })}</span>
              </div>
            </div>
            {error && <p className="error-line" role="alert">{error}</p>}
          </Panel>

          <div className="form-foot">
            <button className="btn secondary" onClick={back} style={{ flex: 1 }} disabled={busy}>
              Cancel
            </button>
            <button className="btn" onClick={save} style={{ flex: 2 }} disabled={busy || exposed === null}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
